import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as authService from '@/services/auth/authService';
import type { User, ProfilePermissions } from '@/types';
import { hasFullStageAccess } from '@/lib/profile-access';

/**
 * Authentication Context and Hook
 * Manages authentication state globally
 */

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  hasPermission: (permission: keyof ProfilePermissions) => boolean;
  isInStage: (stageId: string | number | undefined) => boolean;
  canViewFinancialValues: boolean;
  canEditFinancialValues: boolean;
  canCancelPackages: boolean;
  canRemovePackageSplit: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const isAuth = authService.isAuthenticated();

      if (isAuth) {
        const storedUser = authService.getStoredUser();

        if (storedUser) {
          setUser(storedUser);
        } else {
          // If no stored user, fetch from API
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      // Clear invalid auth data
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    } finally {
      setIsLoading(false);
    }
  };

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (credentials: { username: string; password: string }) =>
      authService.login(credentials),
    onSuccess: (data) => {
      setUser(data.user);
      toast.success('Login realizado com sucesso!');
    },
    onError: (error: any) => {
      console.error('Login error:', error);
      toast.error('Falha no login. Verifique suas credenciais.');
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      setUser(null);
      queryClient.clear(); // Clear all cached queries
      toast.success('Logout realizado com sucesso!');
    },
    onError: (error: any) => {
      console.error('Logout error:', error);
      // Still clear local state even if API call fails
      setUser(null);
      queryClient.clear();
    },
  });

  const login = async (username: string, password: string) => {
    await loginMutation.mutateAsync({ username, password });
  };

  const logout = async () => {
    await logoutMutation.mutateAsync();
  };

  // Permission checker function
  const hasPermission = (permission: keyof ProfilePermissions): boolean => {
    if (!user) return false;
    if (user.profile?.isAdmin) return true;
    return user.profile?.permissions?.[permission] || false;
  };

  // Stage access checker — admins have access to all stages
  const isInStage = (stageId: string | number | undefined): boolean => {
    if (!stageId || !user) return false;
    if (hasFullStageAccess(user.profile)) return true;
    const allowed = user.profile?.allowedStages ?? [];
    return allowed.some(s => String(s.id) === String(stageId));
  };

  // Commonly used permission as direct property
  const canViewFinancialValues = hasPermission('viewFinancialValues');
  const canEditFinancialValues = hasPermission('editFinancialValues');
  const canCancelPackages = hasPermission('cancelPackage');
  const canRemovePackageSplit = hasPermission('removePackageSplit');

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    checkAuth,
    hasPermission,
    isInStage,
    canViewFinancialValues,
    canEditFinancialValues,
    canCancelPackages,
    canRemovePackageSplit,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
